import useDebouncedValue from "hooks/useDebouncedValue";
import useSearchParams from "hooks/useSearchParams";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

const useAuditTrailFilters = () => {
	const [firstPage, setFirstPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [page, setPage] = useState(1);
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [eventInput, setEventInput] = useState("");
	const [search, setSearch] = useState("");
	const [event, setEvent] = useState("");
	const [domain, setDomain] = useState("");

	const debouncedSearch = useDebouncedValue(searchInput);
	const debouncedEvent = useDebouncedValue(eventInput);

	const { getParam, setParam, deleteParam, hasParam } = useSearchParams();
	const { replace } = useHistory();
	const { pathname } = useLocation();
	const hasHydrated = useRef(false);

	useEffect(() => {
		if (hasParam("page")) {
			setPage(getParam("page")?.length > 0 ? getParam("page") : 1);
		}
		if (hasParam("rows")) {
			setRowsPerPage(getParam("rows")?.length > 0 ? getParam("rows") : 20);
		}
		if (hasParam("first_page")) {
			setFirstPage(getParam("first_page")?.length > 0 ? getParam("first_page") : 0);
		}
		if (hasParam("from_date")) {
			setFromDate(getParam("from_date")?.length > 0 ? getParam("from_date") : "");
		}
		if (hasParam("to_date")) {
			setToDate(getParam("to_date")?.length > 0 ? getParam("to_date") : "");
		}
		if (hasParam("search")) {
			const value = getParam("search")?.length > 0 ? getParam("search") : "";
			setSearchInput(value);
			setSearch(value);
		}
		if (hasParam("domain")) {
			setDomain(getParam("domain")?.length > 0 ? getParam("domain") : "");
		}
		if (hasParam("event")) {
			const value = getParam("event")?.length > 0 ? getParam("event") : "";
			setEventInput(value);
			setEvent(value);
		}

		hasHydrated.current = true;

		return () => {
			deleteParam("page");
			deleteParam("rows");
			deleteParam("first_page");
			deleteParam("from_date");
			deleteParam("to_date");
			deleteParam("search");
			deleteParam("domain");
			deleteParam("event");
		};
	}, [hasParam, getParam, deleteParam]);

	const replaceInUrl = (queryString) => {
		replace(queryString ? `${pathname}?${queryString}` : pathname);
	};

	const replaceParam = (key, value, setter) => {
		if (!value || value.length <= 0) {
			setter("");
			return replaceInUrl(deleteParam(key));
		}
		setter(value);
		return replaceInUrl(setParam(key, value));
	};

	const resetPaginationInUrl = () => {
		setPage(1);
		setFirstPage(0);
		setParam("page", "1");
		return setParam("first_page", "0");
	};

	const replaceFilterWithPageReset = (key, value, setter) => {
		setter(value);
		resetPaginationInUrl();
		if (!value || value.length <= 0) {
			deleteParam(key);
		} else {
			setParam(key, value);
		}
		replaceInUrl(setParam("first_page", "0"));
	};

	useEffect(() => {
		if (!hasHydrated.current || debouncedSearch === search) {
			return;
		}

		setSearch(debouncedSearch);
		setPage(1);
		setFirstPage(0);

		if (!debouncedSearch) {
			deleteParam("search");
		} else {
			setParam("search", debouncedSearch);
		}
		setParam("page", "1");
		replaceInUrl(setParam("first_page", "0"));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch, search, deleteParam, setParam, pathname, replace]);

	useEffect(() => {
		if (!hasHydrated.current || debouncedEvent === event) {
			return;
		}

		setEvent(debouncedEvent);
		setPage(1);
		setFirstPage(0);

		if (!debouncedEvent) {
			deleteParam("event");
		} else {
			setParam("event", debouncedEvent);
		}
		setParam("page", "1");
		replaceInUrl(setParam("first_page", "0"));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedEvent, event, deleteParam, setParam, pathname, replace]);

	return {
		firstPage,
		rowsPerPage,
		page,
		fromDate,
		toDate,
		searchInput,
		eventInput,
		search,
		domain,
		event,
		changePage: (value) => replaceParam("page", value, setPage),
		changeRowsPerPage: (value) => replaceParam("rows", value, setRowsPerPage),
		changeFirstPage: (value) => replaceParam("first_page", value, setFirstPage),
		changeFromDate: (value) => replaceFilterWithPageReset("from_date", value, setFromDate),
		changeToDate: (value) => replaceFilterWithPageReset("to_date", value, setToDate),
		changeSearchInput: setSearchInput,
		changeEventInput: setEventInput,
		changeDomain: (value) => replaceFilterWithPageReset("domain", value, setDomain),
		clearFilters: () => {
			setFromDate("");
			setToDate("");
			setSearchInput("");
			setEventInput("");
			setSearch("");
			setEvent("");
			setDomain("");
			setPage(1);
			setFirstPage(0);
			deleteParam("from_date");
			deleteParam("to_date");
			deleteParam("search");
			deleteParam("domain");
			deleteParam("event");
			deleteParam("page");
			deleteParam("first_page");
			replaceInUrl("");
		},
	};
};

export default useAuditTrailFilters;
